import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
} from "react";
import {
  NUMERAL_EN,
  NUMERAL_FA,
  formatDate,
  formatDateTime,
  formatFixed,
  formatInt,
  formatNumber,
  formatPercent,
  getNumeralSystem,
  initNumeralSystem,
  localizeDigits,
  readStoredNumeralSystem,
  setNumeralSystem as setStoreNumeralSystem,
  subscribeNumeralSystem,
} from "../../shared/utils/numberLocale";

const NumberFormatContext = createContext(null);

initNumeralSystem(readStoredNumeralSystem());

export function NumberFormatProvider({ children }) {
  const numeralSystem = useSyncExternalStore(
    subscribeNumeralSystem,
    getNumeralSystem,
    getNumeralSystem,
  );

  const setNumeralSystem = useCallback((next) => {
    setStoreNumeralSystem(next);
  }, []);

  const toggleNumeralSystem = useCallback(() => {
    setStoreNumeralSystem(
      getNumeralSystem() === NUMERAL_EN ? NUMERAL_FA : NUMERAL_EN,
    );
  }, []);

  const value = useMemo(
    () => ({
      numeralSystem,
      isPersian: numeralSystem === NUMERAL_FA,
      isLatin: numeralSystem === NUMERAL_EN,
      setNumeralSystem,
      toggleNumeralSystem,
      formatNumber,
      formatInt,
      formatFixed,
      formatPercent,
      formatDate,
      formatDateTime,
      localizeDigits,
    }),
    [numeralSystem, setNumeralSystem, toggleNumeralSystem],
  );

  return (
    <NumberFormatContext.Provider value={value}>
      {children}
    </NumberFormatContext.Provider>
  );
}

/**
 * Subscribe to the global numeral system and get display formatters.
 * Calling this in a component ensures it re-renders when the setting changes.
 */
export function useNumberFormat() {
  const context = useContext(NumberFormatContext);
  const numeralSystem = useSyncExternalStore(
    subscribeNumeralSystem,
    getNumeralSystem,
    getNumeralSystem,
  );

  if (context) return context;

  return {
    numeralSystem,
    isPersian: numeralSystem === NUMERAL_FA,
    isLatin: numeralSystem === NUMERAL_EN,
    setNumeralSystem: setStoreNumeralSystem,
    toggleNumeralSystem: () =>
      setStoreNumeralSystem(
        getNumeralSystem() === NUMERAL_EN ? NUMERAL_FA : NUMERAL_EN,
      ),
    formatNumber,
    formatInt,
    formatFixed,
    formatPercent,
    formatDate,
    formatDateTime,
    localizeDigits,
  };
}

export { NUMERAL_EN, NUMERAL_FA };
